"use client";

import { ReactNode } from "react";
import { Drawer, DrawerContent, DrawerFooter, DrawerTrigger } from "./drawer";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn } from "@/lib/utils";

interface ResponsivePopoverProps {
  trigger?: ReactNode;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  footer?: ReactNode;
  direction?: "right" | "bottom";
  sideOffset?: number;
  disabled?: boolean;
  avoidCollisions?: boolean;
}

export function ResponsivePopover({
  trigger,
  children,
  open,
  onOpenChange,
  className,
  footer,
  direction = "right",
  sideOffset = 12,
  disabled = false,
  avoidCollisions = true,
}: ResponsivePopoverProps) {
  const isMobile = useMediaQuery("(max-width: 640px)");

  function PopoverFooter({ ...props }: React.ComponentProps<"div">) {
    return <div data-slot="popover-footer" {...props} />;
  }

  const PopoverComponent = isMobile ? Drawer : Popover;
  const PopoverContentComponent = isMobile ? DrawerContent : PopoverContent;
  const PopoverTriggerComponent = isMobile ? DrawerTrigger : PopoverTrigger;
  const PopoverFooterComponent = isMobile ? DrawerFooter : PopoverFooter;

  const contentClassName = cn(`p-4 md:p-0 w-full md:w-auto`, className);

  const handleOpenChange = (isOpen: boolean) => {
    if (disabled) return;
    onOpenChange?.(isOpen);
  };

  return (
    <PopoverComponent
      open={open && !disabled}
      onOpenChange={handleOpenChange}
      {...(isMobile ? { direction: direction } : {})}
    >
      <PopoverTriggerComponent
        asChild
        disabled={disabled}
        className={cn(disabled && `pointer-events-none`)}
      >
        {trigger}
      </PopoverTriggerComponent>
      <PopoverContentComponent
        {...(!isMobile
          ? { sideOffset: sideOffset, avoidCollisions: avoidCollisions }
          : {})}
        className={contentClassName}
      >
        {children}
        {footer ? (
          <PopoverFooterComponent>{footer}</PopoverFooterComponent>
        ) : null}
      </PopoverContentComponent>
    </PopoverComponent>
  );
}
